/**
 * SRP island loader: hydrates the server-rendered SrpApp tree in place (SRP islands rework
 * Phase 2). The server renders the full interactive SrpApp into #srp-ssr (see
 * layouts/LayoutDefault.tsx's SRP branch) instead of a separate static-only tree; this file calls
 * hydrateRoot() directly on that same container once the SrpApp chunk is ready, instead of the old
 * pattern of mounting a second, separate React tree into an empty sibling #srp-app and swapping
 * visibility once it was ready. That swap was the SRP's dominant Lighthouse LCP problem: replacing
 * an already-painted static element with a new one made Chrome's LCP tracker discard the good
 * early paint timestamp for the late one the swap produced. Hydrating the same DOM node in place
 * means no new paint ever happens for content that didn't actually change, so the original fast
 * timestamp stands. SrpApp is still loaded dynamically to keep the initial bundle smaller.
 * Reuses the same root on client-side navigation to avoid calling hydrateRoot/createRoot twice on
 * the same container.
 */
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { getReactRootSentryOptions } from '../../utils/react/react-root-sentry-options';
import type { Root } from 'react-dom/client';
import type { SrpViewModel } from '../../features/srp/SrpViewModel';
import { loadTopEmployersStripDeduped } from '../../utils/srp/fetch-top-employers-strip-api';
import { loadTopRolesStripDeduped } from '../../utils/srp/fetch-top-roles-strip-api';
import {
  scheduleIdleCallbackWithFallback,
  cancelIdleScheduleHandle,
  type IdleScheduleHandle,
} from '../../utils/browser/scheduleIdleCallbackWithFallback';
import type { SessionUser } from '../../types/auth';
import type { CookieMatchProfile } from '../../utils/shared/client-preferences';
import type { PageContextClient } from 'vike/types';

/**
 * Worst-case cap (ms) passed to requestIdleCallback's `timeout` for the deferred hydrate path.
 * NOT a fixed delay — under normal load the browser goes idle within ms of paint and hydration
 * starts then. This only forces hydration by this deadline for a page that stays continuously
 * busy and never goes idle on its own (interaction still overrides immediately regardless of this
 * value). Deferring is safe now in a way it wasn't for the old swap-based mount: hydration never
 * repaints already-correct content, so however long this waits has no LCP cost — it only affects
 * when the page becomes interactive, the same tradeoff every deferred-hydration pattern makes.
 */
const DEFER_MOUNT_FALLBACK_MS = 2500;

/**
 * Kicks off the SrpApp chunk download. Idempotent: safe to call more than once (import() itself
 * caches the module by specifier), memoized here so hydrateSrpIsland can await the same in-flight
 * promise instead of a fresh one.
 */
let srpAppPrefetchPromise: Promise<typeof import('../../features/srp/SrpApp')> | null = null;
function prefetchSrpApp(): void {
  if (srpAppPrefetchPromise) return;
  srpAppPrefetchPromise = import('../../features/srp/SrpApp');
}

/** Reuse root on client-side navigation so we never call hydrateRoot/createRoot twice on the same container. */
let srpRoot: Root | null = null;
let srpRootContainer: HTMLElement | null = null;

function buildSrpAppTree(
  SrpApp: typeof import('../../features/srp/SrpApp')['SrpApp'],
  viewModel: SrpViewModel,
  initialUser: SessionUser | null,
  initialHasBackofficeAccess: boolean,
  initialMatchProfileCookieFallback: CookieMatchProfile | null,
  initialExperiments: Record<string, string>,
): React.ReactElement {
  return React.createElement(SrpApp, {
    initialViewModel: viewModel,
    mode: 'interactive',
    initialUser,
    initialHasBackofficeAccess,
    initialMatchProfileCookieFallback,
    isClientSide: true,
    initialExperiments,
  });
}

async function hydrateSrpIsland(
  container: HTMLElement,
  initialViewModel: SrpViewModel,
  initialUser: SessionUser | null,
  initialHasBackofficeAccess: boolean,
  initialMatchProfileCookieFallback: CookieMatchProfile | null,
  initialExperiments: Record<string, string>,
): Promise<void> {
  prefetchSrpApp();
  const { SrpApp } = await srpAppPrefetchPromise!;

  // Don't block hydration on these — they're a nice-to-have strip, not required for the page to
  // be interactive. Kicked off in the background below and patched in via a second render once
  // they resolve, instead of holding up hydration behind an extra network round-trip.
  const needsEmployers = !initialViewModel.location && !initialViewModel.topEmployersStrip?.length;
  const needsRoles = !initialViewModel.location && !initialViewModel.topRolesStrip?.length;

  const tree = buildSrpAppTree(SrpApp, initialViewModel, initialUser, initialHasBackofficeAccess, initialMatchProfileCookieFallback, initialExperiments);

  if (needsEmployers || needsRoles) {
    Promise.all([
      needsEmployers ? loadTopEmployersStripDeduped() : Promise.resolve(null),
      needsRoles ? loadTopRolesStripDeduped() : Promise.resolve(null),
    ]).then(([extraEmployers, extraRoles]) => {
      if (srpRootContainer !== container || (!extraEmployers?.length && !extraRoles?.length)) return;
      const updatedViewModel = {
        ...initialViewModel,
        ...(extraEmployers && extraEmployers.length > 0 ? { topEmployersStrip: extraEmployers } : {}),
        ...(extraRoles && extraRoles.length > 0 ? { topRolesStrip: extraRoles } : {}),
      };
      srpRoot?.render(
        buildSrpAppTree(SrpApp, updatedViewModel, initialUser, initialHasBackofficeAccess, initialMatchProfileCookieFallback, initialExperiments),
      );
    });
  }

  if (srpRoot && srpRootContainer === container) {
    srpRoot.render(tree);
    return;
  }

  if (srpRoot && srpRootContainer) {
    srpRoot.unmount();
    srpRoot = null;
    srpRootContainer = null;
  }

  // hydrateRoot, not createRoot: the server already rendered this exact tree into `container` (see
  // LayoutDefault.tsx's SRP branch) — this attaches event listeners to the existing DOM instead of
  // building and painting a new one. getReactRootSentryOptions() wires onRecoverableError to
  // Sentry, so any hydration mismatch this doesn't fully eliminate shows up as real production
  // telemetry (SRP islands rework Phase 2 plan's confidence-gap section) instead of only being
  // caught by manual testing of a handful of account states.
  srpRoot = hydrateRoot(container, tree, getReactRootSentryOptions());
  srpRootContainer = container;
}

/**
 * Unmount the SRP island root synchronously. Must be called by `+onRenderClient.ts` before it
 * creates/renders a *different* React root over `#root` (an ancestor of `#srp-ssr`) — otherwise
 * React tears `#srp-ssr` out of the document without ever running srpRoot's own unmount effects
 * (e.g. MapView's Mapbox cleanup), leaving a live map instance whose container silently vanished.
 */
export function unmountSrpIsland(): void {
  if (srpRoot) {
    srpRoot.unmount();
    srpRoot = null;
    srpRootContainer = null;
  }
}

export interface RunSrpIslandLoaderOptions {
  /** When true (e.g. we skipped root hydration), hydrate immediately instead of deferring. */
  mountImmediately?: boolean;
}

export function runSrpIslandLoader(
  pageContext: PageContextClient,
  options: RunSrpIslandLoaderOptions = {}
): void {
  const appEl = document.getElementById('srp-ssr');
  if (!appEl) return;

  const data = (pageContext as { data?: { srpViewModel?: SrpViewModel } })?.data;
  const initialViewModel = data?.srpViewModel;
  if (!initialViewModel) {
    // Full data should always be present now (pageContext.data flows through unslimmed via
    // passToClient — see pages/index/+onAfterRenderHtml.ts). If it's missing, there's nothing
    // valid to hydrate against; leave the server-rendered static content as-is rather than
    // mounting an empty/mismatched tree.
    console.error('[srp.client] pageContext.data.srpViewModel missing — skipping hydration');
    return;
  }

  let mounted = false;
  let fallbackHandle: IdleScheduleHandle | null = null;

  const initialUser = (pageContext?.user ?? null) as SessionUser | null;
  const initialHasBackofficeAccess = Boolean((pageContext as { hasBackofficeAccess?: boolean })?.hasBackofficeAccess);
  const initialMatchProfileCookieFallback =
    (data as { matchProfileCookieFallback?: CookieMatchProfile | null })?.matchProfileCookieFallback ?? null;
  const initialExperiments = (pageContext as { experiments?: Record<string, string> })?.experiments ?? {};

  const doMount = (): void => {
    if (mounted) return;
    mounted = true;
    if (fallbackHandle !== null) {
      cancelIdleScheduleHandle(fallbackHandle);
      fallbackHandle = null;
    }
    void hydrateSrpIsland(appEl, initialViewModel, initialUser, initialHasBackofficeAccess, initialMatchProfileCookieFallback, initialExperiments);
  };

  const onInteraction = (): void => {
    doMount();
    document.removeEventListener('pointerdown', onInteraction);
    document.removeEventListener('keydown', onInteraction);
    document.removeEventListener('focus', onInteraction, true);
  };

  document.addEventListener('pointerdown', onInteraction, { once: true, passive: true });
  document.addEventListener('keydown', onInteraction, { once: true, passive: true });
  document.addEventListener('focus', onInteraction, { once: true, capture: true });

  if (options.mountImmediately) {
    doMount();
  } else {
    // Start hydrating as soon as the browser is idle. Under normal load that's within
    // milliseconds of paint, not a fixed wait — the timeout above is only a worst-case cap for a
    // page that stays continuously busy. A real interaction before idle fires still hydrates
    // immediately via the listeners above.
    fallbackHandle = scheduleIdleCallbackWithFallback(doMount, { timeout: DEFER_MOUNT_FALLBACK_MS });
  }
}
